import {ScrollArea} from '@/components/ui/scroll-area/ScrollArea'
import {Scrollbar} from '@/components/ui/scrollbar/Scrollbar'
import {PatientExtended} from '@/features/patients/types'
import {PatientDetailsContactsSection} from '@/features/patients/components/patient-details-contacts-section/PatientDetailsContactsSection'
import {PatientDetailsMedicalInfoSection} from '@/features/patients/components/patient-details-medical-info-section/PatientDetailsMedicalInfoSection'
import {PatientDetailsPrivacySection} from '@/features/patients/components/patient-details-privacy-section/PatientDetailsPrivacySection'
import {useTranslation} from '@/translations/i18n'
import {DetailsInformationWarningsBanner} from '@/components/commons/details-information-warnings-banner/DetailsInformationWarningBanner'
import {BannerAlert} from '@/components/commons/banner-alert/BannerAlert'
import {Divider} from '@/components/ui/divider/Divider'
import {useRef} from 'react'
import {PatientDetailsPrivacyLegacy} from '@/features/patients/components/patient-details-privacy-legacy/PatientDetailsPrivacyLegacy'
import {Helmet} from 'react-helmet'
import {useOutletContext} from 'react-router-dom'
import {PatientDetailsNotesSection} from '@/features/patients/components/patient-details-notes-section/PatientDetailsNotesSection'
import {usePatientDetailsStore} from '@/features/patients/store/usePatientDetailsStore'
import {ScrollProvider, useScrollContext} from '@/hooks/useScrollContext'
import {PatientDetailsPersonalInfoSection} from '@/features/patients/components/patient-details-personal-info-section/PatientDetailsPersonalInfoSection'
import {PatientDetailsVademecum} from '@/features/patients/components/patient-details-vademecum/PatientDetailsVademecum'

export const PatientDetailsInformation = () => {
    const {t} = useTranslation()

    const {patient} = useOutletContext<{patient: PatientExtended}>()

    return (
        <>
            <Helmet title={t('patients:details_info_meta_title', {fullName: patient.fullName})} />
            <ScrollProvider>
                <PatientDetailsInformationContent patient={patient} />
            </ScrollProvider>
        </>
    )
}

const PatientDetailsInformationContent = ({patient}: {patient: PatientExtended}) => {
    const isErrorBannerVisible = usePatientDetailsStore(state => state.isErrorBannerVisible)
    const setIsErrorBannerVisible = usePatientDetailsStore(state => state.setIsErrorBannerVisible)

    const errorBannerRef = useRef<HTMLDivElement>(null)
    const {scrollContainerRef} = useScrollContext()

    return (
        <ScrollArea scrollbar={<Scrollbar />} viewportProps={{ref: scrollContainerRef}}>
            {isErrorBannerVisible && (
                <>
                    <BannerAlert ref={errorBannerRef} onClose={() => setIsErrorBannerVisible(false)} />
                    <Divider direction="horizontal" />
                </>
            )}
            {patient.fieldsToCompile.length > 0 && (
                <DetailsInformationWarningsBanner fieldsToCompile={patient.fieldsToCompile} />
            )}
            <>
                <PatientDetailsPersonalInfoSection />
                <PatientDetailsContactsSection />
                <PatientDetailsMedicalInfoSection />
                <PatientDetailsPrivacySection />
                {patient.privacyA ||
                    patient.privacyB ||
                    patient.privacyC ||
                    (patient.privacyD && <PatientDetailsPrivacyLegacy patient={patient} />)}
                {(patient.vademecum || patient.project?.isOpeningEventCompleted) && <PatientDetailsVademecum />}
                <PatientDetailsNotesSection patient={patient} />
            </>
        </ScrollArea>
    )
}
